import { useEffect, useMemo, useRef } from "react"

import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import { toast } from "sonner"

import { api } from "@/lib/api-client"
import { queryKeys } from "@/lib/query-keys"
import { useParserJobsStore } from "@/stores/parser-jobs-store"

type ParserJobStatus = "pending" | "running" | "completed" | "failed" | "cancelled"

type TrackedParserJob = {
  jobId: string
  sessionId: string
  status: ParserJobStatus
  errorMessage: string | null
}

type JobStatusApiResponse = {
  job_id: string
  session_id: string
  status: ParserJobStatus
  error_message: string | null
}

const POLL_INTERVAL_MS = 2000

async function getJobStatus(jobId: string): Promise<TrackedParserJob | null> {
  try {
    const queryParams = new URLSearchParams()
    queryParams.set("job_id", jobId)

    const response = (await api.get(
      `/parser/job-status?${queryParams.toString()}`
    )) as JobStatusApiResponse

    return {
      jobId: response.job_id,
      sessionId: response.session_id,
      status: response.status,
      errorMessage: response.error_message,
    }
  } catch {
    return null
  }
}

export function ParserJobToastListener() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()

  const activeJobsById = useParserJobsStore((state) => state.activeJobsById)
  const removeActiveJob = useParserJobsStore((state) => state.removeActiveJob)

  const trackedJobIds = useMemo(() => Object.keys(activeJobsById), [activeJobsById])
  const handledJobIdsRef = useRef<Set<string>>(new Set())

  const { data: trackedJobs } = useQuery({
    queryKey: ["parserTrackedJobs", trackedJobIds],
    queryFn: async () => {
      const jobStatuses = await Promise.all(trackedJobIds.map((jobId) => getJobStatus(jobId)))
      return jobStatuses.filter((job): job is TrackedParserJob => job !== null)
    },
    enabled: trackedJobIds.length > 0,
    refetchInterval: trackedJobIds.length > 0 ? POLL_INTERVAL_MS : false,
    staleTime: 0,
  })

  useEffect(() => {
    if (!trackedJobs) return

    for (const job of trackedJobs) {
      if (job.status === "pending" || job.status === "running") continue
      if (handledJobIdsRef.current.has(job.jobId)) continue

      handledJobIdsRef.current.add(job.jobId)
      removeActiveJob(job.jobId)

      void queryClient.invalidateQueries({
        queryKey: queryKeys.fullSession(job.sessionId),
      })
      void queryClient.invalidateQueries({ queryKey: ["parserActiveJobs"] })

      const isOnSession = location.pathname === `/ai-parser/chat/${job.sessionId}`
      if (isOnSession) continue

      const viewSessionAction = {
        label: t("pages.ai_parser.toasts.view_session"),
        onClick: () => navigate(`/ai-parser/chat/${job.sessionId}`),
      }

      if (job.status === "completed") {
        toast.success(t("pages.ai_parser.toasts.parser_data_updated"), {
          duration: 10000,
          action: viewSessionAction,
        })
        continue
      }

      if (job.status === "failed") {
        toast.error(job.errorMessage || t("pages.ai_parser.toasts.parser_error"), {
          duration: 10000,
          action: viewSessionAction,
        })
        continue
      }

      toast.info(t("pages.ai_parser.toasts.parser_cancelled"), {
        duration: 10000,
        action: viewSessionAction,
      })
    }
  }, [trackedJobs, location.pathname, navigate, queryClient, removeActiveJob, t])

  return null
}
